import React, { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Openlift.css";

/*
 * Accueil — OPENlift / vitrine produit.
 * Le composant et la route /openlift sont conserves.
 * Utilise le visuel de produit deja present : public/images/OPENliftBack.png.
 * Aucune statistique ni donnee en direct n'est simulee.
 */
const CONTENT = {
  fr: {
    eyebrow: "OPENlift · Plateforme IoT",
    title: "Votre parc d'ascenseurs, sous contrôle.",
    accent: "sous contrôle.",
    lead:
      "Depuis une interface simple et centralisée, visualisez l'état de vos ascenseurs, consultez les événements et accédez aux informations dont vos équipes ont besoin.",
    features: [
      "Supervision en temps réel",
      "Suivi des événements et des anomalies",
      "Accès depuis le terrain ou le bureau"
    ],
    cta: "Voir OPENlift en détail",
    imageLabel: "APERÇU DU PRODUIT · INTERFACE EXISTANTE",
    imageAlt: "Interfaces OPENlift : tableau de bord sur ordinateur et suivi sur smartphone",
    imageCaption: "Illustration de l'interface OPENlift — données présentées à titre d'exemple",
    itemOne: "Tableau de bord centralisé",
    itemTwo: "Informations à distance"
  },
  en: {
    eyebrow: "OPENlift · IoT Platform",
    title: "Your elevator fleet, under control.",
    accent: "under control.",
    lead:
      "From a simple, centralized interface, view the status of your elevators, review events and access the information your teams need.",
    features: [
      "Real-time monitoring",
      "Event and anomaly tracking",
      "Access from the field or the office"
    ],
    cta: "See OPENlift in detail",
    imageLabel: "PRODUCT PREVIEW · EXISTING INTERFACE",
    imageAlt: "OPENlift interfaces: desktop dashboard and mobile tracking app",
    imageCaption: "OPENlift interface illustration — example data displayed",
    itemOne: "Centralized dashboard",
    itemTwo: "Remote information"
  }
};

function ArrowIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14m-7-7 7 7-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">
      <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">
      <rect x="3.5" y="3.5" width="17" height="17" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 9.3h17M10 9.3v11.2" stroke="currentColor" strokeWidth="1.6" />
      <path d="m13.2 16.3 2.1-2.3 1.7 1.5 2.1-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DistanceIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 6.7V12l3.6 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Openlift() {
  const { t, i18n } = useTranslation();
  const productRef = useRef(null);
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const fallback = CONTENT[language] || CONTENT.fr;

  const translate = (key, value) => t(`openlift.home.${key}`, { defaultValue: value });
  const eyebrow = translate("eyebrow", fallback.eyebrow);
  const title = translate("title", fallback.title);
  const lead = translate("lead", fallback.lead);
  const cta = translate("cta", fallback.cta);
  const imageAlt = translate("img_alt", fallback.imageAlt);
  const imageLabel = translate("image_label", fallback.imageLabel);
  const imageCaption = translate("image_caption", fallback.imageCaption);
  const itemOne = translate("item_one", fallback.itemOne);
  const itemTwo = translate("item_two", fallback.itemTwo);
  const features = fallback.features.map((value, index) =>
    translate(`feature_${index + 1}`, value)
  );

  const accentIndex = title.toLocaleLowerCase().lastIndexOf(fallback.accent.toLocaleLowerCase());
  const hasAccent = accentIndex >= 0 && accentIndex + fallback.accent.length === title.length;

  useEffect(() => {
    const node = productRef.current;
    if (!node || typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    node.classList.add("openlift-home__product--waiting");
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          node.classList.add("openlift-home__product--visible");
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      node.classList.remove("openlift-home__product--waiting");
      node.classList.remove("openlift-home__product--visible");
    };
  }, []);

  return (
    <section className="openlift-home" aria-labelledby="openlift-home-title">
      <div className="openlift-home__container">
        <div className="openlift-home__grid">
          <div className="openlift-home__copy">
            <p className="openlift-home__eyebrow">{eyebrow}</p>
            <h2 id="openlift-home-title" className="openlift-home__title">
              {hasAccent ? (
                <>
                  {title.slice(0, accentIndex).trimEnd()}{" "}
                  <span className="openlift-home__title-accent">{title.slice(accentIndex)}</span>
                </>
              ) : title}
            </h2>
            <p className="openlift-home__lead">{lead}</p>

            <ul className="openlift-home__features">
              {features.map((feature, index) => (
                <li className="openlift-home__feature" key={index}>
                  <span className="openlift-home__check"><CheckIcon /></span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <NavLink to="/openlift" className="openlift-home__button">
              <span>{cta}</span>
              <ArrowIcon />
            </NavLink>
          </div>

          <div className="openlift-home__visual" ref={productRef}>
            <figure className="openlift-home__figure">
              <div className="openlift-home__product-frame">
                <img
                  src="/images/OPENliftBack2.png"
                  alt={imageAlt}
                  className="openlift-home__image"
                  width="1920"
                  height="1080"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </figure>
            <div className="openlift-home__highlights" aria-label={language === "fr" ? "Fonctions de la plateforme" : "Platform features"}>
              <div className="openlift-home__highlight">
                <DashboardIcon />
                <span>{itemOne}</span>
              </div>
              <div className="openlift-home__highlight">
                <DistanceIcon />
                <span>{itemTwo}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
